import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart, computeCustomPrice, customSpecLabel } from '../context/CartContext';
import { Button, Input, Card, Steps, Progress, Tag, message, DatePicker, Select, Divider, Result, Typography, Space, Grid, Modal, ConfigProvider, Collapse } from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, ShoppingCartOutlined,
  CopyOutlined, CheckCircleFilled, StarFilled, InstagramFilled, FacebookFilled, ShareAltOutlined,
  GoogleOutlined, LogoutOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { encodeInsight } from '../utils/insightCode';
import { calculateWuxing, WUXING_EMOJI, WUXING_COLORS } from '../utils/wuxingCalculator';
import { calculateNumerology, NUMBER_MEANINGS } from '../utils/numerologyCalculator';
import {
  getWuxingRecommendation, getNumerologyRecommendation,
  fetchCrystalRecommendations,
  WUXING_COLOR_MAP, EFFECT_COLOR_MAP,
} from '../utils/colorRecommendation';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

// localStorage 暫存：客人填寫進度與內容（刷新後可留在同一頁、資料還在）
const DRAFT_KEY = 'custom_order_draft_v3';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 草稿 24 小時後自動失效
const loadDraft = () => {
  try {
    localStorage.removeItem('custom_order_draft_v2'); // 清掉舊版結構（步驟數已改，避免卡在不存在的步驟）
    const raw = JSON.parse(localStorage.getItem(DRAFT_KEY));
    if (!raw || typeof raw !== 'object') return {};
    if (raw.savedAt && Date.now() - raw.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return {};
    }
    return raw;
  } catch { return {}; }
};

// ==================== 常數 ====================

const BIRTH_HOURS = [
  { value: '0', label: '子時 (23:00-01:00)' },
  { value: '2', label: '丑時 (01:00-03:00)' },
  { value: '4', label: '寅時 (03:00-05:00)' },
  { value: '6', label: '卯時 (05:00-07:00)' },
  { value: '8', label: '辰時 (07:00-09:00)' },
  { value: '10', label: '巳時 (09:00-11:00)' },
  { value: '12', label: '午時 (11:00-13:00)' },
  { value: '14', label: '未時 (13:00-15:00)' },
  { value: '16', label: '申時 (15:00-17:00)' },
  { value: '18', label: '酉時 (17:00-19:00)' },
  { value: '20', label: '戌時 (19:00-21:00)' },
  { value: '22', label: '亥時 (21:00-23:00)' },
];

const WRIST_SIZES = [];
for (let i = 13; i <= 22; i += 0.5) {
  WRIST_SIZES.push(i);
}

const COLOR_HEX_MAP = {
  '紅色': '#f5222d', '橙色': '#fa8c16', '黃色': '#fadb14',
  '綠色': '#52c41a', '藍色': '#1890ff', '紫色': 'var(--brand-primary)',
  '粉色': '#eb2f96', '黑色': '#333', '白色': '#d9d9d9',
  '金色': '#d4a017', '銀色': '#a0a0a0', '棕色': '#8b4513',
  '青色': '#13c2c2', '深紅': '#a8071a',
};
const LIGHT_COLORS = new Set(['白色', '黃色', '金色', '銀色']);
const ALL_COLORS = Object.keys(COLOR_HEX_MAP);
const colorTagStyle = (c) => {
  const bg = COLOR_HEX_MAP[c];
  if (!bg) return {};
  return { background: bg, color: LIGHT_COLORS.has(c) ? '#333' : '#fff', border: c === '白色' ? '1px solid #d9d9d9' : 'none' };
};

// ==================== 樣式卡片組件 ====================

function OptionCard({ selected, onClick, children, style }) {
  return (
    <Card
      hoverable
      onClick={onClick}
      style={{
        border: selected ? '2px solid var(--brand-primary)' : '2px solid #f0f0f0',
        background: selected ? '#F4F5FB' : '#fff',
        cursor: 'pointer',
        transition: 'all 0.3s',
        ...style,
      }}
      bodyStyle={{ padding: '16px 20px' }}
    >
      {children}
      {selected && (
        <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, color: 'var(--brand-primary)', fontSize: 18 }} />
      )}
    </Card>
  );
}

// ==================== 五行雷達圖（純 SVG，無外部依賴） ====================

const WUXING_RADAR_ORDER = ['木', '火', '土', '金', '水'];

function WuxingRadar({ scores, icons = {} }) {
  // 畫布比繪圖區寬，左右各留邊距，避免最左(水)/最右(火)的標籤被裁掉
  const W = 300;
  const H = 280;
  const cx = W / 2;   // 150
  const cy = H / 2;   // 140
  const R = 76;
  const labelR = R + 22; // 98
  const els = WUXING_RADAR_ORDER;
  const n = els.length;
  // 以最高分（極端值）為滿格，讓最強元素頂到邊、各元素相對比例更明顯
  const maxVal = Math.max(...els.map((e) => scores[e] || 0)) || 1;

  const angle = (i) => (-90 + i * (360 / n)) * (Math.PI / 180);
  const point = (i, r) => [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  const polyAt = (factor) => els.map((_, i) => point(i, R * factor).join(',')).join(' ');
  const dataPoly = els.map((e, i) => point(i, R * ((scores[e] || 0) / maxVal)).join(',')).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: 340, display: 'block', margin: '0 auto' }}>
      {/* 背景同心多邊形 */}
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <polygon key={f} points={polyAt(f)} fill="none" stroke="#eee" strokeWidth={1} />
      ))}
      {/* 軸線 */}
      {els.map((_, i) => {
        const [x, y] = point(i, R);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#eee" strokeWidth={1} />;
      })}
      {/* 相剋五芒星：跨一格連線（外圈相鄰=相生，星線=相剋） */}
      {els.map((_, i) => {
        const [x1, y1] = point(i, R);
        const [x2, y2] = point((i + 2) % n, R);
        return <line key={`k${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#DDE0F1" strokeWidth={1} strokeDasharray="3 3" />;
      })}
      {/* 資料區塊 */}
      <polygon points={dataPoly} fill="rgba(34,41,87,0.18)" stroke="#222957" strokeWidth={2} strokeLinejoin="round" />
      {/* 頂點圓點 */}
      {els.map((e, i) => {
        const [x, y] = point(i, R * ((scores[e] || 0) / maxVal));
        return <circle key={e} cx={x} cy={y} r={3.5} fill={WUXING_COLORS[e]} stroke="#fff" strokeWidth={1} />;
      })}
      {/* 軸標籤（有上傳元素圖用圖，否則 emoji + 元素 + 分數） */}
      {els.map((e, i) => {
        const [lx, ly] = point(i, labelR);
        const cos = Math.cos(angle(i));
        const anchor = Math.abs(cos) < 0.3 ? 'middle' : cos > 0 ? 'start' : 'end';
        const icon = icons[e];
        const IS = 16;
        if (icon) {
          return (
            <g key={e}>
              <image href={icon} x={lx - IS / 2} y={ly - IS - 4} width={IS} height={IS} preserveAspectRatio="xMidYMid meet" />
              <text x={lx} y={ly + 9} textAnchor="middle" dominantBaseline="middle" fontSize={13} fill="#595959">
                <tspan fontWeight={600}>{e}</tspan>
                <tspan dx={2} fill="#222957">{scores[e]}</tspan>
              </text>
            </g>
          );
        }
        return (
          <text key={e} x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle"
            fontSize={13} fill="#595959">
            <tspan fontSize={15}>{WUXING_EMOJI[e]}</tspan>
            <tspan dx={2} fontWeight={600}>{e}</tspan>
            <tspan dx={2} fill="#222957">{scores[e]}</tspan>
          </text>
        );
      })}
    </svg>
  );
}

// ==================== 主組件 ====================

// 客製手鏈的固定商品 ID（與後端一致）
const CUSTOM_PRODUCT_ID = 900000;

export default function CustomOrder() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const { add: addToCart } = useCart();

  // 後台 iframe 預覽：preview=1 關掉毛玻璃；step=<key> 直接呈現指定步驟（供預覽各步驟圖片）
  const _pv = new URLSearchParams(window.location.search);
  const previewLite = _pv.get('preview') === '1';
  const previewStep = previewLite ? _pv.get('step') : null;

  // 表單狀態（初始值優先從 localStorage 暫存還原）
  const [draft0] = useState(loadDraft);
  const [currentStep, setCurrentStep] = useState(draft0.currentStep || 0);
  const [phone, setPhone] = useState(draft0.phone || '');
  const [stylePreference, setStylePreference] = useState(draft0.stylePreference ?? null);
  const [colorMethod, setColorMethod] = useState(draft0.colorMethod ?? null);
  const [birthday, setBirthday] = useState(draft0.birthday ? dayjs(draft0.birthday) : null);
  const [birthTime, setBirthTime] = useState(draft0.birthTime ?? null);
  const [wuxingResult, setWuxingResult] = useState(draft0.wuxingResult ?? null);
  const [numerologyResult, setNumerologyResult] = useState(draft0.numerologyResult ?? null);
  const [colorStrategy, setColorStrategy] = useState(draft0.colorStrategy ?? null);
  const [colorResult, setColorResult] = useState(draft0.colorResult ?? null);
  const [selectedColors, setSelectedColors] = useState(draft0.selectedColors || []);
  const [colorFilter, setColorFilter] = useState('全部');
  const [chainStyle, setChainStyle] = useState(draft0.chainStyle ?? null);
  const [circleDesign, setCircleDesign] = useState(draft0.circleDesign ?? null);
  const [wristSize, setWristSize] = useState(draft0.wristSize ?? null);
  const [specialRequirements, setSpecialRequirements] = useState(draft0.specialRequirements || '');
  const [orderCode, setOrderCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false); // 客製化條款：強制勾選同意才能加入購物車
  const [crystalRecs, setCrystalRecs] = useState(draft0.crystalRecs || {});
  const [crystalRecsLoading, setCrystalRecsLoading] = useState(false);
  const [crystalRecsWithPhotos, setCrystalRecsWithPhotos] = useState(draft0.crystalRecsWithPhotos || {});

  // 手鏈推薦
  const [braceletRecs, setBraceletRecs] = useState([]);
  const [braceletRecsLoading, setBraceletRecsLoading] = useState(false);
  const [braceletRecsLoaded, setBraceletRecsLoaded] = useState(false);
  const [braceletRecsSeen, setBraceletRecsSeen] = useState([]);
  const [braceletRecsTotalMatches, setBraceletRecsTotalMatches] = useState(0);
  const [selectedBracelet, setSelectedBracelet] = useState(draft0.selectedBracelet ?? null);
  const [odooUrl, setOdooUrl] = useState('');

  // 水晶挑選（客製專屬模式）
  const [allCrystalsByColor, setAllCrystalsByColor] = useState({});
  const [allCrystalsLoading, setAllCrystalsLoading] = useState(false);
  const [selectedCrystals, setSelectedCrystals] = useState(draft0.selectedCrystals || []);
  const [previewCrystal, setPreviewCrystal] = useState(null);
  const [activeCrystalColor, setActiveCrystalColor] = useState(null); // 選水晶頁目前的顏色分頁
  const [zeroColorWarn, setZeroColorWarn] = useState(null); // 送出時未選水晶的顏色清單（提醒用）
  const [wristGuideOpen, setWristGuideOpen] = useState(false);

  // 下訂：Google 登入 + 配送 + 付款
  const [customer, setCustomer] = useState(null); // {email, name}
  const [logisticsType, setLogisticsType] = useState('cvs'); // 只提供超商店到店
  const [cvsSubType, setCvsSubType] = useState(draft0.cvsSubType || 'UNIMARTC2C');
  const [cvsStore, setCvsStore] = useState(draft0.cvsStore ?? null); // {id,name,addr}
  const [paymentMethod] = useState('ecpay'); // 只提供線上付款（綠界）
  const [payResult, setPayResult] = useState(null); // 'ok' | 'cancel' | null
  const [pricing, setPricing] = useState({ item: 699, ship_cvs: 60, ship_home: 100 });
  const [myOrders, setMyOrders] = useState(null); // 登入後：自己的訂單
  const [myOrdersLoading, setMyOrdersLoading] = useState(false);

  // 表單圖片
  const [formImages, setFormImages] = useState({});
  const [social, setSocial] = useState({});
  const [theme, setTheme] = useState({ primary: 'var(--brand-primary)', secondary: 'var(--brand-secondary)' });
  // 品牌 IG 帳號（完成頁聯絡入口用，從已連結的 Meta 狀態取得）
  const [igUsername, setIgUsername] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/api/settings/form-images`)
      .then((r) => r.ok ? r.json() : {})
      .then(setFormImages)
      .catch(() => {});
    fetch(`${API_BASE}/api/settings/social`)
      .then((r) => r.ok ? r.json() : {})
      .then(setSocial)
      .catch(() => {});
    fetch(`${API_BASE}/api/settings/theme`)
      .then((r) => r.ok ? r.json() : {})
      .then((d) => { if (d && d.primary) setTheme({ primary: d.primary, secondary: d.secondary || 'var(--brand-secondary)' }); })
      .catch(() => {});
    fetch(`${API_BASE}/api/settings/pricing`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && d.item != null) setPricing(d); })
      .catch(() => {});
    // Odoo URL for product images
    fetch(`${API_BASE}/api/production/status`)
      .then((r) => r.ok ? r.json() : {})
      .then((d) => { if (d.odoo_url) setOdooUrl(d.odoo_url); })
      .catch(() => {});
    // 品牌 IG 帳號（公開端點，用於完成頁「私訊我們」）
    fetch(`${API_BASE}/api/auth/meta/status`)
      .then((r) => r.ok ? r.json() : {})
      .then((d) => { if (d.ig_username) setIgUsername(d.ig_username); })
      .catch(() => {});
  }, []);

  // 任一內容/進度變更即寫回 localStorage；訂單送出成功(已有代號)或走到完成頁後清除暫存
  // → 完成頁重新整理就回到開始頁，不再停留在進度中
  useEffect(() => {
    if (orderCode || steps[currentStep]?.key === 'complete') {
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      return;
    }
    const snapshot = {
      currentStep, phone, stylePreference, colorMethod,
      birthday: birthday ? birthday.toISOString() : null,
      birthTime, wuxingResult, numerologyResult, colorStrategy, colorResult,
      selectedColors, chainStyle, circleDesign, wristSize,
      specialRequirements, selectedBracelet, selectedCrystals,
      crystalRecs, crystalRecsWithPhotos,
      logisticsType, cvsSubType, cvsStore, paymentMethod,
      savedAt: Date.now(),
    };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot)); } catch {}
  }, [
    currentStep, phone, stylePreference, colorMethod, birthday, birthTime,
    wuxingResult, numerologyResult, colorStrategy, colorResult, selectedColors,
    chainStyle, circleDesign, wristSize, specialRequirements,
    selectedBracelet, selectedCrystals, crystalRecs, crystalRecsWithPhotos, orderCode,
    logisticsType, cvsSubType, cvsStore, paymentMethod,
  ]);

  // 登入後載入自己的訂單（訂單綁在 email 下）
  useEffect(() => {
    if (!customer) { setMyOrders(null); return; }
    const token = (() => { try { return localStorage.getItem('customer_token'); } catch { return null; } })();
    if (!token) return;
    setMyOrdersLoading(true);
    fetch(`${API_BASE}/api/bracelet-orders/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setMyOrders(d.items || []))
      .catch(() => setMyOrders([]))
      .finally(() => setMyOrdersLoading(false));
  }, [customer]);

  // ==================== 步驟定義 ====================

  // 動態步驟列表（根據 colorMethod 有不同分支）
  const steps = useMemo(() => {
    const base = [
      { key: 'welcome', title: '歡迎' },
      { key: 'method', title: '分析方式' },
      { key: 'analysis_result', title: '分析結果' },
      { key: 'select_colors', title: '選擇顏色' },
      { key: 'bracelet_recommend', title: '推薦手鏈' },
      { key: 'crystal_select', title: '水晶挑選' },
      { key: 'wrist', title: '手圍尺寸' },
      { key: 'specs', title: '手鏈規格' },
    ];
    // 功效導向：沒有五行/靈數分析，跳過分析結果步驟
    if (colorMethod === 'effect') {
      return base.filter((s) => s.key !== 'analysis_result');
    }
    return base;
  }, [colorMethod]);

  const currentKey = steps[currentStep]?.key;

  // 應付金額估算（實際金額以後端建立訂單時為準）
  const orderAmountBreakdown = useMemo(() => {
    const ship = pricing.ship_cvs || 0; // 超商店到店運費
    let item;
    if (selectedBracelet) {
      // 選現有設計款：用該款的價錢
      item = selectedBracelet.min_variant_price || pricing.item || 0;
    } else {
      // 客製：依款式基礎價 + 雙圈加價 + 超尺寸加價（與後端一致）
      const base = { elastic: pricing.elastic, adjustable: pricing.adjustable, magnetic: pricing.magnetic }[chainStyle];
      item = base || pricing.adjustable || pricing.item || 0;
      if (circleDesign === 'double') item += (pricing.double_add || 0);
      const w = parseFloat(wristSize);
      if (!Number.isNaN(w) && w > (pricing.oversize_threshold || 17)) item += (pricing.oversize_add || 0);
    }
    return { item, ship, total: item + ship };
  }, [pricing, chainStyle, circleDesign, wristSize, selectedBracelet]);

  // 後台預覽：直接跳到指定步驟，讓上方進度條與下方導航也一起呈現
  useEffect(() => {
    if (previewStep) {
      const i = steps.findIndex((s) => s.key === previewStep);
      if (i >= 0) setCurrentStep(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 處理登入/選店/付款導回的網址參數（OAuth、綠界地圖、綠界付款都會導回本頁）
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    let changed = false;

    // 1) Google 登入回來：帶 ctoken
    const ctoken = q.get('ctoken');
    if (ctoken) {
      try { localStorage.setItem('customer_token', ctoken); } catch {}
      q.delete('ctoken'); changed = true;
    }
    // 2) 超商選店回來：帶門市資訊
    if (q.get('cvs') === '1') {
      const store = {
        id: q.get('storeId') || '', name: q.get('storeName') || '', addr: q.get('storeAddr') || '',
      };
      if (store.id) {
        setLogisticsType('cvs');
        if (q.get('subType')) setCvsSubType(q.get('subType'));
        setCvsStore(store);
      }
      ['cvs', 'storeId', 'storeName', 'storeAddr', 'subType'].forEach((k) => q.delete(k));
      changed = true;
    }
    // 3) 綠界付款導回
    const payR = q.get('pay_result');
    if (payR != null) {
      setPayResult(payR === '1' ? 'ok' : 'fail');
      if (q.get('order')) setOrderCode(q.get('order'));
      q.delete('pay_result'); q.delete('order'); changed = true;
    }
    if (q.get('pay_cancel')) { setPayResult('cancel'); q.delete('pay_cancel'); changed = true; }

    // 有登入 token 就抓客人資訊
    const tok = ctoken || (() => { try { return localStorage.getItem('customer_token'); } catch { return null; } })();
    if (tok) {
      fetch(`${API_BASE}/api/auth/google/customer/me`, { headers: { Authorization: `Bearer ${tok}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((info) => { if (info) setCustomer(info); else { try { localStorage.removeItem('customer_token'); } catch {} } })
        .catch(() => {});
    }

    // 清掉網址上的一次性參數
    if (changed) {
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== 操作 ====================

  const goNext = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    window.scrollTo(0, 0);
  }, [steps.length]);

  const goPrev = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 0));
    window.scrollTo(0, 0);
  }, []);

  const goToStep = useCallback((stepKey) => {
    const idx = steps.findIndex((s) => s.key === stepKey);
    if (idx >= 0) setCurrentStep(idx);
    window.scrollTo(0, 0);
  }, [steps]);

  // 從商品頁「我想客製」帶入的參考手鍊：設為 selectedBracelet 並直接進微調（手圍）步驟
  const refConsumed = useRef(false);
  useEffect(() => {
    const ref = location.state && location.state.refBracelet;
    if (ref && !refConsumed.current) {
      refConsumed.current = true;
      setSelectedBracelet(ref);
      goToStep('wrist');
      // 清掉 navigation state，避免重整又重新帶入
      navigate('/custom', { replace: true, state: {} });
    }
  }, [location.state, goToStep, navigate]);

  // 執行五行計算（同時算出配色推薦，供分析結果頁的策略按鈕使用）
  const doWuxingCalc = useCallback(() => {
    if (!birthday || !birthTime) return;
    const d = birthday;
    const result = calculateWuxing(d.year(), d.month() + 1, d.date(), parseInt(birthTime));
    setWuxingResult(result);
    const supRec = getWuxingRecommendation(result, 'supplement');
    const strRec = getWuxingRecommendation(result, 'strengthen');
    const seen = new Set();
    const allRecs = [];
    for (const r of [...supRec.recommendations, ...strRec.recommendations]) {
      if (!seen.has(r.element)) { seen.add(r.element); allRecs.push(r); }
    }
    setColorResult({
      strategy: 'all', elements: allRecs.map((r) => r.element),
      recommendations: allRecs, _supplement: supRec, _strengthen: strRec,
    });
    goNext();
  }, [birthday, birthTime, goNext]);

  // 執行生命靈數計算（同時算出配色推薦）
  const doNumerologyCalc = useCallback(() => {
    if (!birthday) return;
    const d = birthday;
    const result = calculateNumerology(d.year(), d.month() + 1, d.date());
    setNumerologyResult(result);
    setColorResult(getNumerologyRecommendation(result));
    goNext();
  }, [birthday, goNext]);

  // 載入手鏈推薦
  const loadBraceletRecs = useCallback(async (colors, excludeIds = []) => {
    setBraceletRecsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/bracelet-orders/recommend-bracelets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ colors, count: 2, exclude_ids: excludeIds }),
      });
      if (!res.ok) throw new Error('推薦請求失敗');
      const data = await res.json();
      setBraceletRecs(data.bracelets || []);
      setBraceletRecsTotalMatches(data.total_matches || 0);
      setBraceletRecsSeen((prev) => [...prev, ...(data.bracelets || []).map((b) => b.id)]);
      return (data.bracelets || []).length;
    } catch (err) {
      console.warn('手鏈推薦失敗:', err);
      setBraceletRecs([]);
      return 0;
    } finally {
      setBraceletRecsLoading(false);
      setBraceletRecsLoaded(true);
    }
  }, []);

  // 進入 bracelet_recommend 步驟時，依「已選顏色」載入推薦；沒有對應款式就自動跳到選水晶
  useEffect(() => {
    if (currentKey === 'bracelet_recommend' && !braceletRecsLoaded && !braceletRecsLoading) {
      const colors = [...new Set(selectedColors)];
      if (colors.length > 0) {
        loadBraceletRecs(colors).then((found) => {
          if (!found) goToStep('crystal_select');
        });
      } else {
        goToStep('crystal_select');
      }
    }
  }, [currentKey, selectedColors, braceletRecsLoaded, braceletRecsLoading, loadBraceletRecs, goToStep]);

  // 保險：只要在推薦頁、已載入卻沒有任何推薦款式，一律自動跳到選水晶（不讓用戶停在空狀態按按鈕）
  useEffect(() => {
    if (currentKey === 'bracelet_recommend' && braceletRecsLoaded && !braceletRecsLoading && braceletRecs.length === 0) {
      goToStep('crystal_select');
    }
  }, [currentKey, braceletRecsLoaded, braceletRecsLoading, braceletRecs.length, goToStep]);

  // 載入全部水晶（依已選顏色，供「每色最多 3 顆」挑選）
  const loadAllCrystals = useCallback(async () => {
    setAllCrystalsLoading(true);
    try {
      const colors = [...new Set(selectedColors)];
      if (colors.length === 0) return;
      const res = await fetch(`${API_BASE}/api/crystal-colors/recommend-with-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ colors, count: 100 }),
      });
      if (res.ok) {
        const data = await res.json();
        setAllCrystalsByColor(data.recommendations || {});
      }
    } catch (err) {
      console.warn('loadAllCrystals failed:', err);
    } finally {
      setAllCrystalsLoading(false);
    }
  }, [selectedColors]);

  useEffect(() => {
    if (currentKey === 'crystal_select' && Object.keys(allCrystalsByColor).length === 0 && !allCrystalsLoading) {
      loadAllCrystals();
    }
  }, [currentKey, allCrystalsByColor, allCrystalsLoading, loadAllCrystals]);

  // 進入統一選色步驟時，五行/靈數先用分析結果預選顏色（之後可再增減）
  useEffect(() => {
    if (currentKey === 'select_colors' && selectedColors.length === 0 &&
        (colorMethod === 'wuxing' || colorMethod === 'numerology')) {
      const seed = [...new Set(getStrategyColors())];
      if (seed.length > 0) setSelectedColors(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  // 載入動態水晶推薦（附帶照片）
  const loadCrystalRecs = useCallback(async (colors) => {
    setCrystalRecsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/crystal-colors/recommend-with-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ colors: [...new Set(colors)], count: 3 }),
      });
      if (res.ok) {
        const data = await res.json();
        const nameOnly = {};
        const withPhotos = {};
        for (const [color, items] of Object.entries(data.recommendations || {})) {
          nameOnly[color] = items.map((i) => i.crystal_name);
          withPhotos[color] = items;
        }
        setCrystalRecs(nameOnly);
        setCrystalRecsWithPhotos(withPhotos);
        setCrystalRecsLoading(false);
        return;
      }
    } catch (err) {
      console.warn('recommend-with-photos 失敗，fallback:', err);
    }
    // Fallback to original endpoint
    const recs = await fetchCrystalRecommendations([...new Set(colors)]);
    setCrystalRecs(recs);
    setCrystalRecsLoading(false);
  }, []);

  // 切換選色（統一選色步驟用；多選）
  const toggleColor = useCallback((color) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  }, []);

  // 依五行/靈數的分析 + 配色策略，算出建議顏色（用於統一選色步驟的預選種子）
  const getStrategyColors = useCallback(() => {
    if (!colorResult) return [];
    if (colorResult.recommendations) { // 五行
      if (colorStrategy && colorResult[`_${colorStrategy}`]) {
        return colorResult[`_${colorStrategy}`].recommendations.flatMap((r) => r.colors);
      }
      return colorResult.recommendations.flatMap((r) => r.colors);
    }
    if (colorResult.lifePath) { // 靈數
      if (colorStrategy === 'strengthen') return colorResult.lifePath.colors || [];
      return [
        ...(colorResult.lifePath.colors || []),
        ...(colorResult.missingRecommendations?.flatMap((m) => m.colors) || []),
      ];
    }
    return [];
  }, [colorResult, colorStrategy]);

  // 選水晶頁「下一步」：若有顏色一顆都沒選，先提醒（補選 / 跳過）
  const handleCrystalNext = useCallback(() => {
    // 只檢查「有水晶可選」的顏色（用實際顯示的資料源 allCrystalsByColor）；沒庫存的不強制
    const hasInventory = (color) => (allCrystalsByColor[color] || []).length > 0;
    const zeros = selectedColors.filter((color) => hasInventory(color) && !selectedCrystals.some((c) => c.color === color));
    if (zeros.length > 0) { setZeroColorWarn(zeros); return; }
    goNext();
  }, [selectedColors, selectedCrystals, allCrystalsByColor, goNext]);

  // 完成設計 → 加入購物車（統一走共用購物車結帳，不再自行付款/物流）
  const handleAddToCart = useCallback(async () => {
    if (!chainStyle || !circleDesign) { message.warning('請先選好手鏈款式與圈數'); return; }
    if (!wristSize) { message.warning('請先選擇手圍尺寸'); return; }
    if (!termsAgreed) { message.warning('請先勾選同意客製化條款（不適用 7 日鑑賞期）'); return; }

    setSubmitting(true);
    try {
      // 蒐集全部設計資料進 custom_detail
      const detail = {
        color_method: colorMethod,
        // 保留生日/時辰，讓訂單頁能重建命理詳細頁分享連結
        birthday: birthday ? birthday.format('YYYY-MM-DD') : null,
        birth_time: birthTime,
        wuxing_result: wuxingResult,
        numerology_result: numerologyResult,
        effect_choice: colorMethod === 'effect' ? (colorFilter !== '全部' ? colorFilter : null) : null,
        intuitive_color: null,
        color_strategy: colorStrategy,
        color_result: colorResult,
        selected_colors: selectedColors.length > 0 ? selectedColors : [],
        selected_crystals: selectedCrystals.length > 0 ? selectedCrystals : [],
        chain_style: chainStyle,
        circle_design: circleDesign,
        wrist_size: wristSize,
        special_requirements: specialRequirements || null,
        // 客製化條款同意紀錄（法律告知證據）
        terms_agreed: true,
        terms_agreed_at: new Date().toISOString(),
      };

      // 價格前端試算（後端建立訂單時會權威重算），pricing 來自 /api/settings/pricing
      const price = computeCustomPrice(detail, pricing);

      const customItem = {
        product_id: CUSTOM_PRODUCT_ID,
        variant_id: null,
        name: '客製手鏈',
        spec_label: customSpecLabel(detail),
        price,
        qty: 1,
        image_url: null,
        is_custom: true,
        custom_detail: detail,
      };

      addToCart(customItem, 1);

      // 清除客製暫存（草稿 / localStorage），避免回到未完成狀態
      try { localStorage.removeItem(DRAFT_KEY); } catch {}

      message.success('已加入購物車');
      navigate('/cart');
    } catch (err) {
      message.error('加入購物車失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  }, [chainStyle, circleDesign, wristSize, termsAgreed, colorMethod, wuxingResult, numerologyResult, colorFilter, colorStrategy, colorResult, selectedColors, selectedCrystals, specialRequirements, pricing, addToCart, navigate]);

  // 前往 Google 登入（帶回本頁）
  const startCustomerLogin = useCallback(() => {
    window.location.href = `${API_BASE}/api/auth/google/customer/login?next=${encodeURIComponent('/custom')}`;
  }, []);

  const logoutCustomer = useCallback(() => {
    try { localStorage.removeItem('customer_token'); } catch {}
    setCustomer(null);
  }, []);

  // ==================== 步驟渲染 ====================

  // 根據螢幕尺寸選擇背景圖（電腦版/手機版，fallback 舊版 key）
  // 表單背景圖（電腦/手機版，fallback 舊 key）
  const bgImage = formImages[isMobile ? 'bg_mobile' : 'bg_desktop'] || formImages.hero || null;

  // 五行小圖示：有後台上傳的元素圖就用圖，否則退回 emoji
  const EL_KEY = { '木': 'wood', '火': 'fire', '土': 'earth', '金': 'metal', '水': 'water' };
  const elIcon = (el, size = 14) => {
    const img = formImages[`wuxing_${EL_KEY[el]}`];
    if (img) return <img src={img} alt={el}
      style={{ width: size, height: size, objectFit: 'contain', verticalAlign: '-0.18em', display: 'inline-block' }}
      onError={(e) => { e.target.style.display = 'none'; }} />;
    return <span style={{ fontSize: size }}>{WUXING_EMOJI[el]}</span>;
  };
  const wuxingIcons = Object.fromEntries(
    ['木', '火', '土', '金', '水'].map((el) => [el, formImages[`wuxing_${EL_KEY[el]}`]]).filter(([, u]) => u),
  );

  const containerStyle = {
    maxWidth: 600,
    margin: '0 auto',
    padding: isMobile ? '16px' : '24px',
    position: 'relative',
    zIndex: 1,
  };

  const cardGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: 12,
  };

  // 通用步驟插圖
  const renderStepImage = (imageKey, alt) => {
    const url = formImages[imageKey];
    if (!url) return null;
    return (
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <img src={url} alt={alt} style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 12 }}
          onError={(e) => { e.target.parentNode.style.display = 'none'; }} />
      </div>
    );
  };

  // Step: 歡迎（垂直置中於視窗）
  const renderWelcome = () => (
    <div
      style={{
        // 內容區外層有 paddingTop 16 + paddingBottom 40（=56），扣掉才能真正置中
        minHeight: 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: 600,
        margin: '0 auto',
        padding: isMobile ? '16px' : '24px',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 28, position: 'relative', zIndex: 1, paddingTop: 12 }}>
        {formImages.logo ? (
          <img src={formImages.logo} alt="星河耀眼" style={{ height: 116, objectFit: 'contain', marginBottom: 4 }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--brand-primary)', marginBottom: 6 }}>
            <StarFilled style={{ fontSize: 34 }} />
            <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: 3 }}>星河耀眼</span>
          </div>
        )}
        <div style={{ fontSize: 12.5, color: '#727DC9', letterSpacing: 3, marginBottom: 18 }}>SHINING&nbsp;STARS</div>
        <Title level={3} style={{ margin: 0, color: '#222A5A', fontWeight: 800, letterSpacing: 1 }}>
          客製您的專屬能量手鏈
        </Title>
        <Paragraph style={{ color: '#3B489B', fontSize: 15, marginTop: 10, marginBottom: 0, lineHeight: 1.8 }}>
          結合命理分析與水晶能量，<br />
          為此刻的您，挑一條剛剛好的光。
        </Paragraph>
        {formImages.step_welcome && (
          <img src={formImages.step_welcome} alt="" style={{ width: '100%', borderRadius: 16, marginTop: 20, boxShadow: '0 10px 30px -8px rgba(34,41,87,0.22)' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </div>

      <Button
        type="primary"
        size="large"
        block
        onClick={goNext}
        style={{ height: 48, fontSize: 16, position: 'relative', zIndex: 1 }}
      >
        開始設計我的手鏈
      </Button>

      {/* 「我的訂單」已移除：客製第一頁不再顯示，會員相關統一至右上角「會員中心」 */}

      {(social.facebook || social.instagram || social.line) && (
        <div style={{ textAlign: 'center', marginTop: 28, position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18 }}>
            {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)' }} aria-label="Facebook"><FacebookFilled style={{ fontSize: 26 }} /></a>}
            {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)' }} aria-label="Instagram"><InstagramFilled style={{ fontSize: 26 }} /></a>}
            {social.line && <a href={social.line} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', height: 26, padding: '0 10px', borderRadius: 13, background: '#06C755', color: '#fff', fontSize: 13, fontWeight: 700 }} aria-label="LINE">LINE</a>}
          </div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 10 }}>© 星河耀眼 ShiningStars</div>
        </div>
      )}
    </div>
  );

  // Step: 色調風格
  const renderStylePreference = () => (
    <div style={containerStyle}>
      {renderStepImage('step_style', '色調風格')}
      <Title level={4} style={{ textAlign: 'center', marginBottom: 24 }}>選擇您喜歡的色調風格</Title>
      <div style={{ ...cardGridStyle, gridTemplateColumns: '1fr' }}>
        {[
          { key: 'harmonious', label: '和諧色調', desc: '柔和、溫暖的色彩搭配，帶來舒適平靜的感覺', gradient: 'linear-gradient(135deg, #fce4ec, #E5E7F5, #e8eaf6)' },
          { key: 'neutral', label: '中性色調', desc: '低飽和度的配色，簡約高雅不張揚', gradient: 'linear-gradient(135deg, #efebe9, #eceff1, #fafafa)' },
          { key: 'vivid', label: '鮮豔色調', desc: '色彩鮮明、對比強烈，展現個性與活力', gradient: 'linear-gradient(135deg, #ffcdd2, #fff9c4, #c8e6c9)' },
        ].map((item) => (
          <OptionCard key={item.key} selected={stylePreference === item.key} onClick={() => setStylePreference(item.key)}>
            <div style={{ background: item.gradient, height: 60, borderRadius: 8, marginBottom: 12 }} />
            <Text strong style={{ fontSize: 16 }}>{item.label}</Text>
            <br />
            <Text type="secondary">{item.desc}</Text>
          </OptionCard>
        ))}
      </div>
    </div>
  );

  // Step: 選擇分析方式
  const renderMethodChoice = () => (
    <div style={containerStyle}>
      {renderStepImage('step_analysis', '分析方式')}
      <Title level={4} style={{ textAlign: 'center', marginBottom: 8 }}>選擇您的配色靈感</Title>
      <Paragraph style={{ textAlign: 'center', color: '#595959', marginBottom: 24 }}>
        我們提供多種方式幫助您找到最適合的手鏈顏色
      </Paragraph>
      <div style={{ ...cardGridStyle, gridTemplateColumns: '1fr' }}>
        {[
          { key: 'wuxing', icon: '🌳', label: '五行分析', desc: '根據您的八字命造，分析五行能量分佈，推薦平衡或強化的色彩' },
          { key: 'numerology', icon: '🔢', label: '生命靈數', desc: '透過生日計算生命靈數，解讀您的核心能量與色彩' },
          { key: 'effect', icon: '✨', label: '說說您的心願', desc: '告訴我們您此刻的願望或狀態，依此為您挑選顏色' },
        ].map((item) => (
          <div key={item.key}>
            <OptionCard selected={colorMethod === item.key} onClick={() => setColorMethod(item.key)}>
              <Space>
                <span style={{ fontSize: 28 }}>{item.icon}</span>
                <div>
                  <Text strong style={{ fontSize: 16 }}>{item.label}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 13 }}>{item.desc}</Text>
                </div>
              </Space>
            </OptionCard>

            {/* 選到五行/靈數時就地展開生日輸入 */}
            {colorMethod === item.key && (item.key === 'wuxing' || item.key === 'numerology') && (
              <Card size="small" style={{ margin: '8px 0 4px', background: '#fafafa', border: '1px solid #E5E7F5' }}>
                <div style={{ marginBottom: item.key === 'wuxing' ? 12 : 0 }}>
                  <Text strong style={{ fontSize: 13 }}>出生日期</Text>
                  <DatePicker
                    style={{ width: '100%', marginTop: 6 }} size="large"
                    placeholder="可直接輸入 20260629 或選擇日期"
                    value={birthday} onChange={setBirthday}
                    // 顯示用第一個格式；輸入時可省略「-」「/」（如 20260629）也能解析
                    format={['YYYY-MM-DD', 'YYYYMMDD', 'YYYY/MM/DD', 'YYYY.MM.DD']}
                    disabledDate={(d) => d && d.isAfter(dayjs())}
                  />
                </div>
                {item.key === 'wuxing' && (
                  <div>
                    <Text strong style={{ fontSize: 13 }}>出生時辰</Text>
                    <Select
                      style={{ width: '100%', marginTop: 6 }} size="large" placeholder="選擇出生時辰"
                      value={birthTime} onChange={setBirthTime} options={BIRTH_HOURS}
                    />
                  </div>
                )}
              </Card>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Step: 分析結果
  const renderAnalysisResult = () => {
    if (colorMethod === 'wuxing' && wuxingResult) {
      return renderWuxingResult();
    }
    if (colorMethod === 'numerology' && numerologyResult) {
      return renderNumerologyResult();
    }
    return null;
  };

  // 五行分析結果
  const renderWuxingResult = () => {
    const { bazi, scores, analysis } = wuxingResult;
    // 依分析結果產生穩定種子，讓形容詞「動態替換」但同一份分析不亂跳
    const seed = Object.values(scores).reduce((a, b) => a + b, 0) + (bazi?.day?.full?.charCodeAt(0) || 0);
    const v = (arr) => arr[Math.abs(seed) % arr.length];

    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <Title level={4} style={{ marginBottom: 0 }}>🌟 您的八字命造分析</Title>
        </div>

        {/* 雷達圖 + 八字命造 + 溫暖解讀（合併一張卡） */}
        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 15, marginBottom: 8, display: 'block' }}>五行能量分佈</Text>
          <WuxingRadar scores={scores} icons={wuxingIcons} />

          {/* 八字命造（雷達圖下方） */}
          <Divider style={{ margin: '12px 0' }}>八字命造</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, textAlign: 'center' }}>
            {[
              { label: '年柱', value: bazi.year.full },
              { label: '月柱', value: bazi.month.full },
              { label: '日柱', value: bazi.day.full },
              { label: '時柱', value: bazi.hour.full },
            ].map((p) => (
              <div key={p.label} style={{ background: '#F4F5FB', borderRadius: 8, padding: '12px 8px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{p.label}</Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--brand-primary)', marginTop: 4 }}>{p.value}</div>
              </div>
            ))}
          </div>

          {/* 溫暖解讀（八字下方，形容詞動態替換） */}
          <Divider style={{ margin: '12px 0' }} />
          <Paragraph style={{ fontSize: 15, marginBottom: 0 }}>
            {analysis.strongest.length > 0 && (
              <>
                <Text>{v(['您天生擁有', '您命格中蘊藏著', '您身上自然流露著', '您與生俱來帶著'])}{v(['豐沛的', '旺盛的', '飽滿的', '充盈的'])}</Text>
                {analysis.strongest.map((el) => (
                  <Tag key={el} color={WUXING_COLORS[el]} style={{ margin: '0 4px' }}>{elIcon(el)} {el}</Tag>
                ))}
                <Text>元素能量，{v(['代表著', '象徵著', '透露出'])}{analysis.strongest.map((e) => WUXING_COLOR_MAP[e]?.trait).join('與')}。</Text>
                <br /><br />
              </>
            )}
            {analysis.weakest.length > 0 && (
              <>
                <Text>{v(['而', '相對地，', '另一方面，'])}</Text>
                {analysis.weakest.map((el) => (
                  <Tag key={el} color={WUXING_COLORS[el]} style={{ margin: '0 4px' }}>{elIcon(el)} {el}</Tag>
                ))}
                <Text>元素{v(['稍弱', '較為含蓄', '略顯不足', '相對輕盈'])}，可以透過水晶來{v(['溫柔補足', '慢慢調和', '補位平衡'])}。</Text>
              </>
            )}
            {analysis.strongest.length === 0 && analysis.weakest.length === 0 && (
              <Text>{v(['您的五行能量相當平衡', '您的五行分布十分均衡', '您的能量調和圓融'])}，可依個人喜好選擇想加強的方向。</Text>
            )}
          </Paragraph>
        </Card>

        {/* 配色策略：兩個按鈕（含元素 + 推薦色），合併原推薦色彩步驟 */}
        {colorResult && colorResult.recommendations && (
          <>
            <Title level={5} style={{ textAlign: 'center', marginTop: 8 }}>您想要怎麼配色？</Title>
            <div style={cardGridStyle}>
              {[
                { key: 'supplement', icon: '🔹', label: '補足較弱的元素', recs: colorResult._supplement?.recommendations || [], avoid: colorResult._supplement?.avoid || [] },
                { key: 'strengthen', icon: '🔸', label: '強化突出的優勢', recs: colorResult._strengthen?.recommendations || [], avoid: colorResult._strengthen?.avoid || [] },
              ].map((opt) => (
                <OptionCard key={opt.key} selected={colorStrategy === opt.key} onClick={() => setColorStrategy(opt.key)}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 26, marginBottom: 6 }}>{opt.icon}</div>
                    <Text strong>{opt.label}</Text>
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                      {opt.recs.map((r) => (
                        <Tag key={r.element} color={WUXING_COLORS[r.element]}>
                          {elIcon(r.element)} {r.element}{r.role === 'generate' ? ' · 相生' : ''}
                        </Tag>
                      ))}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                      {opt.recs.flatMap((r) => r.colors).map((c) => (
                        <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>
                      ))}
                    </div>
                    {opt.avoid.length > 0 && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        相剋宜避免：{opt.avoid.map((a, i) => <span key={a.element}>{i > 0 && '、'}{elIcon(a.element, 13)}{a.element}</span>)}
                      </Text>
                    )}
                  </div>
                </OptionCard>
              ))}
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center', marginTop: 8 }}>
              「相生」是用生我者來補益（如木生火），效果更穩；「相剋」會削弱目標元素，故宜避免。
            </Text>
          </>
        )}

        {/* 五行生剋關係 */}
        <Card style={{ marginTop: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>五行生剋關係</Text>
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>相生（循環滋養）</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginBottom: 12 }}>
            {['木', '火', '土', '金', '水', '木'].map((el, i) => (
              <React.Fragment key={i}>
                <Tag color={WUXING_COLORS[el]} style={{ margin: 0 }}>{elIcon(el)} {el}</Tag>
                {i < 5 && <span style={{ color: '#bbb' }}>→</span>}
              </React.Fragment>
            ))}
          </div>
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>相剋（互相制約）</Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[['木', '土'], ['火', '金'], ['土', '水'], ['金', '木'], ['水', '火']].map(([a, b]) => (
              <Tag key={a} style={{ margin: 0 }}>{elIcon(a, 13)}{a} ⊸ {elIcon(b, 13)}{b}</Tag>
            ))}
          </div>
        </Card>

        {/* 說明 FAQ */}
        <Collapse ghost style={{ marginTop: 4 }}
          items={[
            {
              key: '1', label: '什麼是五行分析？',
              children: <Text type="secondary" style={{ fontSize: 13 }}>
                依出生年月日時的天干地支，計算金、木、水、火、土五種元素的強弱分布，判斷五行是否平衡，並推算對您有利的「喜用神」。
              </Text>,
            },
            {
              key: '2', label: '喜用神是什麼？',
              children: <Text type="secondary" style={{ fontSize: 13 }}>
                指對命主有利的五行元素——能補足不足、平衡過旺的那一行。可用於指導穿戴顏色、配飾選擇等；本表單即依此推薦您的開運色彩。
              </Text>,
            },
            {
              key: '3', label: '五行調和建議有哪些？',
              children: <Text type="secondary" style={{ fontSize: 13 }}>
                除了開運顏色，傳統上還延伸到有利方位、幸運數字、適合職業、補益飲食與有利季節等。本表單聚焦用水晶顏色，為您補足或強化能量。
              </Text>,
            },
          ]}
        />

        {birthday && birthTime != null && (
          <Button block size="large" icon={<ShareAltOutlined />} style={{ marginTop: 16, height: 46 }}
            onClick={() => window.open(`/insight?c=${encodeInsight({ type: 'wuxing', d: birthday.format('YYYY-MM-DD'), t: birthTime })}`, '_blank')}>
            查看完整分析 · 可分享收藏
          </Button>
        )}
      </div>
    );
  };

  // 生命靈數結果
  const renderNumerologyResult = () => {
    const { digits, coreNumbers, missingInsights, lines } = numerologyResult;
    const lp = coreNumbers.lifePath;

    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ marginBottom: 0 }}>🔢 您的生命靈數分析</Title>
        </div>

        {/* 核心能量 */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'center' }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: 'var(--brand-primary)' }}>{lp.value}</div>
              <Title level={5} style={{ margin: '8px 0 4px' }}>命數 {lp.value} — {lp.summary}</Title>
              {lp.isMaster && (
                <Tag color="gold" style={{ marginBottom: 8 }}>大師數 {lp.masterNumber}</Tag>
              )}
              <Paragraph style={{ color: '#595959', marginBottom: 8 }}>
                {lp.strengths}
              </Paragraph>
              <Space wrap>
                {lp.keywords.map((k) => <Tag key={k} color="geekblue">{k}</Tag>)}
              </Space>
            </div>
            {(() => {
              // 形象圖做成「浮動卡片」：投影 + 背後光暈 + 上下浮動（與 InsightDetail 一致）。
              // 優先用後台切好的 9 張單圖（永遠不會歪）；沒有才退回單張九宮格的等分切割。
              const perNum = formImages[`numerology_${lp.value}`];
              const boxStyle = {
                width: isMobile ? 170 : 200, aspectRatio: '2 / 3',
                borderRadius: 14, backgroundRepeat: 'no-repeat',
                boxShadow: '0 18px 36px -10px rgba(34,41,87,0.38), 0 6px 14px -6px rgba(34,41,87,0.25)',
              };
              let inner = null;
              if (perNum) {
                inner = <div role="img" aria-label={lp.summary}
                  style={{ ...boxStyle, backgroundImage: `url(${perNum})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />;
              } else if (formImages.numerology_grid && lp.value >= 1 && lp.value <= 9) {
                const idx = lp.value - 1, col = idx % 3, row = Math.floor(idx / 3);
                inner = <div role="img" aria-label={lp.summary}
                  style={{ ...boxStyle, backgroundImage: `url(${formImages.numerology_grid})`, backgroundSize: '300% 300%', backgroundPosition: `${col * 50}% ${row * 50}%` }} />;
              }
              if (!inner) return null;
              return (
                <div style={{ position: 'relative', width: 'fit-content', margin: '0 auto', paddingTop: 6, flexShrink: 0 }}>
                  {/* 背後光暈，營造浮起的層次 */}
                  <div style={{ position: 'absolute', inset: '-12% -16% -2%', background: 'radial-gradient(60% 55% at 50% 42%, rgba(107,119,199,0.28), rgba(107,119,199,0) 70%)', filter: 'blur(8px)', zIndex: 0, pointerEvents: 'none' }} />
                  <div style={{ position: 'relative', zIndex: 1, animation: 'insightFloat 4.5s ease-in-out infinite' }}>{inner}</div>
                  {/* 地面投影，隨主體浮動 */}
                  <div style={{ width: '62%', height: 14, margin: '10px auto 0', borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(34,41,87,0.22), rgba(34,41,87,0) 70%)', animation: 'insightShadow 4.5s ease-in-out infinite' }} />
                </div>
              );
            })()}
          </div>
        </Card>

        {/* 九宮格（含能量連線）：樣式與 InsightDetail 洞察報告一致——圈層計數、柔化邊框、漸層連線 */}
        <Card style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>數字能量九宮格</Text>
          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
            每個數字在您生日中出現幾次，就用幾層圈圈起來——圈越多層，這份能量越強；空白格是「缺數」課題。三格連成一線即為「天賦連線」（紫線標示）。
          </Paragraph>
          <div style={{ position: 'relative' }}>
            {/* 能量連線疊在格子上 */}
            {lines.length > 0 && (
              <svg viewBox="0 0 3 3" preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 5, pointerEvents: 'none' }}>
                <defs>
                  <linearGradient id="numGridLine" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#949DD6" />
                    <stop offset="100%" stopColor="#eb6fb0" />
                  </linearGradient>
                </defs>
                {lines.map((l) => {
                  const ds = (String(l.combination).match(/[1-9]/g) || []).map(Number);
                  if (ds.length < 2) return null;
                  const pts = ds.map((d) => `${((d - 1) % 3) + 0.5},${Math.floor((d - 1) / 3) + 0.5}`).join(' ');
                  return (
                    <polyline key={l.combination} points={pts} fill="none"
                      stroke="url(#numGridLine)" strokeWidth={0.055} strokeOpacity={0.7}
                      strokeLinecap="round" strokeLinejoin="round" />
                  );
                })}
              </svg>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, position: 'relative', zIndex: 1 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => {
                const count = digits.counts[d];
                const isMissing = count === 0;
                const isLifePath = d === lp.value;
                return (
                  <div
                    key={d}
                    style={{
                      minHeight: 86, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', padding: '8px 6px',
                      borderRadius: 10,
                      background: isLifePath ? '#F4F5FB' : isMissing ? '#fafafa' : '#fff',
                      border: isLifePath ? '2px solid var(--brand-primary)' : isMissing ? '1px dashed #D4D8EE' : '1px solid #f0f0f0',
                      boxShadow: isMissing ? 'none' : '0 1px 4px rgba(34,41,87,0.06)',
                    }}
                  >
                    {isMissing ? (
                      <>
                        <div style={{ fontSize: 26, fontWeight: 700, color: '#D7DAEF' }}>{d}</div>
                        <span style={{ fontSize: 12, color: '#AEB5E0' }}>缺</span>
                      </>
                    ) : (
                      <>
                        {(() => {
                          // 出現幾次 = 幾層圈（封頂 3 層，超過再用「出現 N 次」補充）
                          const rings = Math.min(count, 3);
                          const baseD = 32, step = 9;
                          const outer = baseD + (rings >= 2 ? (rings - 1) * 2 * step : 0);
                          return (
                            <div style={{ position: 'relative', width: outer, height: outer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {count >= 2 && Array.from({ length: rings }).map((_, r) => {
                                const dd = baseD + r * 2 * step;
                                return <span key={r} style={{ position: 'absolute', width: dd, height: dd, borderRadius: '50%', border: '1.5px solid var(--brand-primary)', opacity: 0.55 - r * 0.13 }} />;
                              })}
                              <span style={{ fontSize: 22, fontWeight: 800, color: '#374390', position: 'relative', zIndex: 1 }}>{d}</span>
                            </div>
                          );
                        })()}
                        {count > 3 && <span style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>出現 {count} 次</span>}
                        {isLifePath && <span style={{ fontSize: 12, color: 'var(--brand-primary)', marginTop: 2 }}>命數</span>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* 能量解讀（連線 + 缺數建議 合併） */}
        {(lines.length > 0 || missingInsights.length > 0) && (
          <Card style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>能量解讀</Text>
            {lines.map((l) => (
              <div key={l.combination} style={{ marginBottom: 8 }}>
                <Tag color="blue">{l.label}</Tag>
                <Text type="secondary" style={{ fontSize: 13 }}>{l.description}</Text>
              </div>
            ))}
            {lines.length > 0 && missingInsights.length > 0 && <Divider style={{ margin: '8px 0' }} />}
            {missingInsights.map((m) => (
              <div key={m.digit} style={{ marginBottom: 8 }}>
                <Text strong>缺數 {m.digit} — {m.title}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>{m.suggestion}</Text>
              </div>
            ))}
          </Card>
        )}

        {/* 配色策略：兩個按鈕（強化命數 / 補足缺數） */}
        {colorResult && colorResult.lifePath && (() => {
          const missingColors = colorResult.missingRecommendations?.flatMap((m) => m.colors) || [];
          return (
            <>
              <Title level={5} style={{ textAlign: 'center', marginTop: 8 }}>您想要怎麼配色？</Title>
              <div style={cardGridStyle}>
                <OptionCard selected={colorStrategy === 'strengthen'} onClick={() => setColorStrategy('strengthen')}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 26, marginBottom: 6 }}>🔸</div>
                    <Text strong>強化命數特質</Text>
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                      {(colorResult.lifePath.colors || []).map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)}
                    </div>
                  </div>
                </OptionCard>
                <OptionCard selected={colorStrategy === 'supplement'} onClick={() => setColorStrategy('supplement')}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 26, marginBottom: 6 }}>🔹</div>
                    <Text strong>補足缺數能量</Text>
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                      {missingColors.length > 0
                        ? missingColors.map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)
                        : <Text type="secondary" style={{ fontSize: 13 }}>您的數字能量已很完整</Text>}
                    </div>
                  </div>
                </OptionCard>
              </div>
            </>
          );
        })()}

      </div>
    );
  };

  // Step: 顏色推薦 / 直覺選色 / 功效選擇
  // 水晶 chip（含照片）
  const renderCrystalChips = (colors) => {
    if (crystalRecsLoading) {
      return <Text type="secondary" style={{ fontSize: 13 }}>載入中...</Text>;
    }
    // Collect unique crystals with photo info
    const seen = new Set();
    const crystals = [];
    for (const color of colors) {
      const items = crystalRecsWithPhotos[color] || [];
      for (const item of items) {
        const name = typeof item === 'string' ? item : item.crystal_name;
        if (!seen.has(name)) {
          seen.add(name);
          crystals.push({
            name,
            image_url: typeof item === 'object' ? item.image_url : null,
          });
        }
      }
      // fallback to crystalRecs if no photo data
      if (items.length === 0) {
        for (const name of (crystalRecs[color] || [])) {
          if (!seen.has(name)) {
            seen.add(name);
            crystals.push({ name, image_url: null });
          }
        }
      }
    }
    if (crystals.length === 0) {
      return <Text type="secondary" style={{ fontSize: 13 }}>暫無庫存推薦</Text>;
    }
    return crystals.map((c) => (
      <Tag key={c.name} color="geekblue" style={{ margin: '2px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {c.image_url && (
          <img
            src={c.image_url}
            alt=""
            style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        {c.name}
      </Tag>
    ));
  };

  const renderColorResult = () => {
    // 五行/靈數的顏色推薦結果（選配色策略）
    if (!colorResult) return null;

    return (
      <div style={containerStyle}>
        {renderStepImage('step_result', '顏色推薦')}
        <Title level={4} style={{ textAlign: 'center' }}>
          🌈 推薦色彩方案
        </Title>

        {colorMethod === 'wuxing' && colorResult.recommendations && (
          <>
            {colorResult.recommendations.map((rec) => (
              <Card key={rec.element} style={{ marginBottom: 12 }}>
                <Space align="start">
                  <span style={{ fontSize: 32 }}>{WUXING_EMOJI[rec.element]}</span>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{rec.element}元素</Text>
                    <br />
                    <Text type="secondary">{rec.trait}</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text strong>推薦顏色：</Text>
                      {rec.colors.map((c) => <Tag key={c} style={{ margin: '2px', ...colorTagStyle(c) }}>{c}</Tag>)}
                    </div>
                  </div>
                </Space>
              </Card>
            ))}

            <Title level={5} style={{ textAlign: 'center', marginTop: 16 }}>您想要怎麼配色？</Title>
            <div style={cardGridStyle}>
              <OptionCard selected={colorStrategy === 'supplement'} onClick={() => setColorStrategy('supplement')}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🔹</div>
                  <Text strong>補足較弱的元素</Text>
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                    {(colorResult._supplement?.recommendations || []).flatMap((r) => r.colors).map((c) => (
                      <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>
                    ))}
                  </div>
                </div>
              </OptionCard>
              <OptionCard selected={colorStrategy === 'strengthen'} onClick={() => setColorStrategy('strengthen')}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🔸</div>
                  <Text strong>強化突出的優勢</Text>
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                    {(colorResult._strengthen?.recommendations || []).flatMap((r) => r.colors).map((c) => (
                      <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>
                    ))}
                  </div>
                </div>
              </OptionCard>
            </div>
          </>
        )}

        {colorMethod === 'numerology' && colorResult.lifePath && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 15 }}>
                命數 {colorResult.lifePath.value} — {colorResult.lifePath.title}
              </Text>
              <br />
              <Text type="secondary">{colorResult.lifePath.trait}</Text>
              <div style={{ marginTop: 8 }}>
                <Text strong>推薦顏色：</Text>
                {colorResult.lifePath.colors.map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)}
              </div>
            </Card>
            {colorResult.missingRecommendations?.length > 0 && (
              <Card style={{ marginBottom: 16, background: '#fffbe6', border: '1px solid #ffe58f' }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>缺數補充色彩</Text>
                {colorResult.missingRecommendations.map((m) => (
                    <div key={m.digit} style={{ marginBottom: 8 }}>
                      <Text strong>數字 {m.digit} — {m.title}</Text>
                      <br />
                      {m.colors.map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)}
                    </div>
                ))}
              </Card>
            )}

            {(() => {
              const missingColors = colorResult.missingRecommendations?.flatMap((m) => m.colors) || [];
              return (
                <>
                  <Title level={5} style={{ textAlign: 'center', marginTop: 8 }}>您想要怎麼配色？</Title>
                  <div style={cardGridStyle}>
                    <OptionCard selected={colorStrategy === 'strengthen'} onClick={() => setColorStrategy('strengthen')}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>🔸</div>
                        <Text strong>強化命數特質</Text>
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                          {(colorResult.lifePath?.colors || []).map((c) => (
                            <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>
                          ))}
                        </div>
                      </div>
                    </OptionCard>
                    <OptionCard selected={colorStrategy === 'supplement'} onClick={() => setColorStrategy('supplement')}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>🔹</div>
                        <Text strong>補足缺數能量</Text>
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                          {missingColors.length > 0
                            ? missingColors.map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)
                            : <Text type="secondary" style={{ fontSize: 13 }}>您的數字能量已很完整</Text>}
                        </div>
                      </div>
                    </OptionCard>
                  </div>
                </>
              );
            })()}
          </>
        )}

        {birthday && (
          <Button block size="large" icon={<ShareAltOutlined />} style={{ marginTop: 16, height: 46 }}
            onClick={() => window.open(`/insight?c=${encodeInsight({ type: 'numerology', d: birthday.format('YYYY-MM-DD') })}`, '_blank')}>
            查看完整分析 · 可分享收藏
          </Button>
        )}
      </div>
    );
  };

  // Step: 統一選色（過濾：全部 + 各功效；多選）
  const renderSelectColors = () => {
    const filters = ['全部', ...Object.keys(EFFECT_COLOR_MAP)];
    const colorsForFilter = colorFilter === '全部'
      ? ALL_COLORS
      : (EFFECT_COLOR_MAP[colorFilter]?.colors || []);
    return (
      <div style={containerStyle}>
        <Title level={4} style={{ textAlign: 'center', marginBottom: 4 }}>選擇您的顏色</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 12 }}>
          {colorMethod === 'effect'
            ? '依您此刻的心願／狀態篩選，挑選喜歡的顏色（可多選）'
            : '已依您的分析預選，可再依心願／狀態篩選增減顏色'}
        </Text>

        {/* 已選顏色 */}
        <Card size="small" style={{ marginBottom: 12, background: '#F4F5FB', border: '1px solid #E5E7F5' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            <Text strong style={{ fontSize: 13 }}>已選 {selectedColors.length}：</Text>
            {selectedColors.length === 0
              ? <Text type="secondary" style={{ fontSize: 13 }}>尚未選擇</Text>
              : selectedColors.map((c) => (
                  <Tag key={c} closable style={{ ...colorTagStyle(c), cursor: 'pointer' }}
                    onClose={(e) => { e.preventDefault(); toggleColor(c); }}>{c}</Tag>
                ))}
          </div>
        </Card>

        {/* 功效過濾按鈕 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {filters.map((f) => {
            const active = colorFilter === f;
            const icon = EFFECT_COLOR_MAP[f]?.icon;
            return (
              <Button key={f} size="small" shape="round"
                type={active ? 'primary' : 'default'}
                onClick={() => setColorFilter(f)}
                style={active ? {} : { borderColor: '#BEC3E6', color: 'var(--brand-primary)' }}>
                {icon ? `${icon} ${f}` : f}
              </Button>
            );
          })}
        </div>

        {/* 顏色選擇 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {colorsForFilter.map((c) => {
            const selected = selectedColors.includes(c);
            return (
              <div key={c} onClick={() => toggleColor(c)}
                style={{
                  position: 'relative', cursor: 'pointer', borderRadius: 10,
                  border: selected ? '2px solid var(--brand-primary)' : '2px solid #f0f0f0',
                  background: selected ? '#F4F5FB' : '#fff',
                  padding: '10px 6px', textAlign: 'center', transition: 'all 0.2s',
                }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', margin: '0 auto 6px',
                  background: COLOR_HEX_MAP[c] || '#ccc',
                  border: c === '白色' ? '1px solid #d9d9d9' : 'none',
                }} />
                <Text style={{ fontSize: 13 }}>{c}</Text>
                {selected && (
                  <CheckCircleFilled style={{ position: 'absolute', top: 6, right: 6, color: 'var(--brand-primary)', fontSize: 16 }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Step: 推薦手鏈（依使用者最終選定的顏色）
  const renderBraceletRecommend = () => {
    const handleRefresh = () => {
      const colors = [...new Set(selectedColors)];
      if (colors.length > 0) {
        loadBraceletRecs(colors, braceletRecsSeen);
      }
    };

    const canRefresh = braceletRecsTotalMatches > braceletRecsSeen.length;

    return (
      <div style={containerStyle}>
        <Title level={4} style={{ textAlign: 'center' }}>
          💎 為您挑選的手鏈
        </Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 20 }}>
          根據您的色彩分析，我們精選了風格相近的設計款
        </Text>

        {braceletRecsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">正在為您尋找合適的手鏈...</Text>
          </div>
        ) : braceletRecs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">正在為您準備客製選項…</Text>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
              {braceletRecs.map((b) => (
                <Card
                  key={b.id}
                  hoverable
                  onClick={() => setSelectedBracelet(selectedBracelet?.id === b.id ? null : b)}
                  style={{
                    border: selectedBracelet?.id === b.id ? '2px solid var(--brand-primary)' : '2px solid #f0f0f0',
                    background: selectedBracelet?.id === b.id ? '#F4F5FB' : '#fff',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                  bodyStyle={{ padding: 0 }}
                >
                  {selectedBracelet?.id === b.id && (
                    <CheckCircleFilled style={{ position: 'absolute', top: 12, right: 12, color: 'var(--brand-primary)', fontSize: 22, zIndex: 1 }} />
                  )}
                  <div style={{ width: '100%', height: 220, overflow: 'hidden', background: '#fafafa' }}>
                    <img
                      src={b.image_url || `/api/production/image/bracelets/${b.id}.jpg`}
                      alt={b.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Text strong style={{ fontSize: 15 }}>{b.name}</Text>
                      {b.min_variant_price && (
                        <Text strong style={{ color: 'var(--brand-primary)' }}>NT$ {b.min_variant_price}</Text>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {b.main_stone && <Tag color="geekblue">{b.main_stone}</Tag>}
                      {b.crystal_main_color && <Tag style={colorTagStyle(b.crystal_main_color)}>{b.crystal_main_color}</Tag>}
                      {b.crystal_accent_color && <Tag style={colorTagStyle(b.crystal_accent_color)}>{b.crystal_accent_color}</Tag>}
                    </div>
                    {b.story && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        {b.story.length > 60 ? b.story.slice(0, 60) + '...' : b.story}
                      </Text>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
              {selectedBracelet && selectedBracelet.pinkoi_url && (
                <Button type="primary" size="large" block
                  onClick={() => window.open(selectedBracelet.pinkoi_url, '_blank')}
                  style={{ background: 'var(--brand-primary)', borderColor: 'var(--brand-primary)', maxWidth: 400 }}>
                  就是它了！直接購買
                </Button>
              )}
              {selectedBracelet && (
                <Button size="large" block onClick={() => goToStep('wrist')}
                  style={{ maxWidth: 400, borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}>
                  我喜歡這條，但想微調
                </Button>
              )}
              {canRefresh ? (
                <Button size="large" block onClick={handleRefresh}
                  style={{ maxWidth: 400 }}>
                  我還想看其他設計款
                </Button>
              ) : braceletRecsSeen.length > 0 && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    目前展示款就這些了，我們還在持續設計中 ✨
                  </Text>
                </div>
              )}
              <Button onClick={() => { setSelectedBracelet(null); goNext(); }} style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}>
                我想要完全客製專屬我的光芒 ✨
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Step: 水晶挑選（依已選顏色，每種顏色最多 3 顆）
  const renderCrystalSelect = () => {
    const isSelected = (name) => selectedCrystals.some((c) => c.crystal_name === name);
    const colorCount = (color) => selectedCrystals.filter((c) => c.color === color).length;

    const toggleCrystal = (crystal, color) => {
      if (isSelected(crystal.crystal_name)) {
        setSelectedCrystals((prev) => prev.filter((c) => c.crystal_name !== crystal.crystal_name));
      } else {
        if (colorCount(color) >= 3) {
          message.warning(`「${color}」最多選 3 顆水晶`);
          return;
        }
        setSelectedCrystals((prev) => [...prev, { ...crystal, color }]);
      }
    };

    // 顏色分頁 = 使用者選的顏色；目前分頁顏色
    const active = (activeCrystalColor && selectedColors.includes(activeCrystalColor))
      ? activeCrystalColor : selectedColors[0];
    const activeCrystals = allCrystalsByColor[active] || [];

    return (
      <div style={containerStyle}>
        <Title level={4} style={{ textAlign: 'center' }}>挑選您的專屬水晶</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 12 }}>
          點顏色切換，每種顏色最多挑 3 顆
        </Text>

        {/* 顏色分頁列（各帶 已選/3） */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 12 }}>
          {selectedColors.map((color) => {
            const isActive = color === active;
            const cnt = colorCount(color);
            return (
              <Button key={color} shape="round" size="small"
                type={isActive ? 'primary' : 'default'}
                onClick={() => setActiveCrystalColor(color)}
                style={{ flexShrink: 0, ...(isActive ? {} : { borderColor: cnt > 0 ? 'var(--brand-primary)' : '#d9d9d9', color: cnt > 0 ? 'var(--brand-primary)' : undefined }) }}>
                {color} ({cnt}/3)
              </Button>
            );
          })}
        </div>

        {allCrystalsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">正在載入水晶...</Text>
          </div>
        ) : activeCrystals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Text type="secondary">「{active}」目前沒有庫存水晶，可在下一步直接跳過這個顏色</Text>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {activeCrystals.map((crystal) => {
              const selected = isSelected(crystal.crystal_name);
              return (
                <div key={crystal.crystal_name}
                  style={{
                    position: 'relative', cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
                    border: selected ? '3px solid var(--brand-primary)' : '3px solid transparent',
                    transition: 'all 0.2s',
                  }}>
                  <div onClick={() => toggleCrystal(crystal, active)}
                    style={{ aspectRatio: '1/1', background: '#fafafa' }}>
                    {crystal.image_url ? (
                      <img src={crystal.image_url} alt={crystal.crystal_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', color: '#999', fontSize: 12 }}>
                        無圖片
                      </div>
                    )}
                  </div>

                  {selected && (
                    <div style={{
                      position: 'absolute', top: 4, right: 4, background: 'var(--brand-primary)', borderRadius: '50%',
                      width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CheckCircleFilled style={{ color: '#fff', fontSize: 16 }} />
                    </div>
                  )}

                  <div onClick={(e) => { e.stopPropagation(); setPreviewCrystal(crystal); }}
                    style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'rgba(0,0,0,0.5)', color: '#fff',
                      textAlign: 'center', fontSize: 14, padding: '6px 0', cursor: 'pointer',
                    }}>
                    {crystal.crystal_name}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 送出時：有顏色沒選水晶 → 提醒補選或跳過 */}
        <Modal
          open={!!zeroColorWarn}
          onCancel={() => setZeroColorWarn(null)}
          centered
          title="還有顏色沒挑水晶"
          footer={[
            <Button key="back" onClick={() => setZeroColorWarn(null)}>回去補選</Button>,
            <Button key="skip" type="primary" onClick={() => { setZeroColorWarn(null); goNext(); }}>直接跳過</Button>,
          ]}
        >
          <Paragraph style={{ marginBottom: 4 }}>
            以下顏色還沒挑選水晶：
          </Paragraph>
          <div style={{ marginBottom: 8 }}>
            {(zeroColorWarn || []).map((c) => <Tag key={c} style={colorTagStyle(c)}>{c}</Tag>)}
          </div>
          <Text type="secondary">要回去補選，還是直接跳過這些顏色？</Text>
        </Modal>

        <Modal
          open={!!previewCrystal}
          onCancel={() => setPreviewCrystal(null)}
          footer={null}
          centered
          width={isMobile ? '90vw' : 480}
        >
          {previewCrystal && (
            <div style={{ textAlign: 'center' }}>
              {previewCrystal.image_url ? (
                <img src={previewCrystal.image_url} alt={previewCrystal.crystal_name}
                  style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }} />
              ) : (
                <div style={{ padding: 40, color: '#999' }}>無圖片</div>
              )}
              <div style={{ padding: '12px 0' }}>
                <Text strong style={{ fontSize: 18 }}>{previewCrystal.crystal_name}</Text>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  };

  // Step: 手鏈規格（材質 + 款式 + 圈數 合併為一頁）
  const renderSpecs = () => {
    const twoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 };
    // 合併頁用較矮的圖，避免整頁過長
    const compactImg = (imageKey) => {
      const url = formImages[imageKey];
      if (!url) return null;
      return (
        <div style={{ margin: '-16px -20px 10px', borderRadius: '8px 8px 0 0', overflow: 'hidden' }}>
          <img src={url} alt="" style={{ width: '100%', height: 100, objectFit: 'cover' }}
            onError={(e) => { e.target.parentNode.style.display = 'none'; }} />
        </div>
      );
    };
    const section = (title, value, setter, options) => (
      <>
        <Divider orientation="left" style={{ fontSize: 14, color: 'var(--brand-primary)' }}>{title}</Divider>
        <div style={twoCol}>
          {options.map((item) => (
            <OptionCard key={item.key} selected={value === item.key} onClick={() => setter(item.key)}>
              {compactImg(item.img)}
              <div style={{ textAlign: 'center', padding: '4px 0' }}>
                <Text strong style={{ fontSize: 16 }}>{item.label}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 13 }}>{item.desc}</Text>
              </div>
            </OptionCard>
          ))}
        </div>
      </>
    );

    return (
      <div style={containerStyle}>
        <Title level={4} style={{ textAlign: 'center', marginBottom: 4 }}>手鏈規格</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>
          選擇手鏈款式與圈數設計
        </Text>
        {section('🔗 手鏈款式', chainStyle, setChainStyle, [
          { key: 'elastic', label: '彈性繩款式', desc: '舒適方便、隨手穿戴', img: 'elastic' },
          { key: 'adjustable', label: '調節鏈款式', desc: '精緻優雅、可調鬆緊', img: 'adjustable' },
          { key: 'magnetic', label: '磁扣款式', desc: '磁扣好戴、時尚俐落', img: 'magnetic' },
        ])}
        {section('⭕ 圈數設計', circleDesign, setCircleDesign, [
          { key: 'single', label: '單圈設計', desc: '簡約日常、輕巧舒適', img: 'single_circle' },
          { key: 'double', label: '雙圈設計', desc: '層次豐富、更加華麗', img: 'double_circle' },
        ])}

        {/* 特殊需求（選填，合併於手鏈規格下方） */}
        <Divider orientation="left" style={{ fontSize: 14, color: 'var(--brand-primary)' }}>特殊需求（選填）</Divider>
        <Card style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            有任何額外的需求或偏好都可以在這裡告訴我們，例如：
          </Text>
          <ul style={{ color: '#595959', fontSize: 13 }}>
            <li>想加入特定的吉祥物件（如貔貅、蓮花）</li>
            <li>喜歡特定造型的水晶</li>
            <li>有過敏材質需要避免</li>
            <li>送禮對象的資訊</li>
          </ul>
          <Input.TextArea
            rows={4}
            placeholder="輸入您的特殊需求..."
            value={specialRequirements}
            onChange={(e) => setSpecialRequirements(e.target.value)}
            maxLength={500}
            showCount
          />
        </Card>
      </div>
    );
  };

  // Step: 手圍尺寸
  const renderWristSize = () => (
    <div style={containerStyle}>
      <Title level={4} style={{ textAlign: 'center', marginBottom: 4 }}>📏 手圍尺寸</Title>
      <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>
        告訴我們您的手圍，做出最合適的長度
      </Text>
      <Card style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>測量指南</Text>
        {formImages.wrist_guide ? (
          <>
            <img src={formImages.wrist_guide} alt="手圍量測教學"
              style={{ width: '100%', borderRadius: 8, marginBottom: 8, cursor: 'pointer' }}
              onClick={() => setWristGuideOpen(true)}
              onError={(e) => { e.target.style.display = 'none'; }} />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center' }}>
              點擊圖片可放大查看
            </Text>
            {wristGuideOpen && createPortal(
              <div
                onClick={() => setWristGuideOpen(false)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.92)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'auto', WebkitOverflowScrolling: 'touch',
                }}
              >
                <button
                  onClick={() => setWristGuideOpen(false)}
                  aria-label="關閉"
                  style={{
                    position: 'fixed', top: 16, right: 16, zIndex: 1, width: 40, height: 40,
                    borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)',
                    color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer',
                  }}
                >×</button>
                <img
                  src={formImages.wrist_guide} alt="手圍量測教學"
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: '100%', maxWidth: 900, height: 'auto', touchAction: 'pinch-zoom', display: 'block' }}
                />
              </div>,
              document.body
            )}
          </>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>
            用軟尺或紙條繞手腕一圈（手腕骨上方），量出長度即為手圍。
            一般女生手圍約 14-17cm，男生約 16-20cm。
          </Text>
        )}
      </Card>
      <Select
        style={{ width: '100%', marginBottom: 16 }}
        size="large"
        placeholder="選擇手圍 (cm)"
        value={wristSize}
        onChange={setWristSize}
        options={WRIST_SIZES.map((s) => ({ value: s, label: `${s} cm` }))}
      />
    </div>
  );

  // Step: 綠界付款導回結果
  const renderPayResult = () => {
    if (payResult === 'ok') {
      return (
        <div style={containerStyle}>
          <Result
            status="success"
            title="付款成功，訂單已成立！"
            subTitle="我們已將訂單確認信寄到您的信箱，會盡快為您手作專屬手鏈。"
          />
          {orderCode && (
            <Card style={{ textAlign: 'center', marginBottom: 24 }}>
              <Text type="secondary">您的客製代號</Text>
              <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--brand-primary)', margin: '10px 0', letterSpacing: 2, fontFamily: 'monospace' }}>{orderCode}</div>
              <Button icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(orderCode); message.success('已複製'); }}>複製代號</Button>
            </Card>
          )}
          <Button type="primary" size="large" block style={{ height: 48 }} onClick={() => { window.location.href = '/custom'; }}>返回首頁</Button>
        </div>
      );
    }
    // 失敗 / 取消
    return (
      <div style={containerStyle}>
        <Result
          status="warning"
          title={payResult === 'cancel' ? '您已取消付款' : '付款未完成'}
          subTitle={orderCode
            ? `訂單 ${orderCode} 尚未付款。您可稍後用手機號碼在首頁查詢並重新付款。`
            : '訂單尚未完成付款，您可以重新嘗試。'}
        />
        <Button type="primary" size="large" block style={{ height: 48 }} onClick={() => { window.location.href = '/custom'; }}>返回重新開始</Button>
      </div>
    );
  };

  // ==================== 渲染步驟 ====================

  const renderStep = () => {
    if (payResult) return renderPayResult();
    switch (currentKey) {
      case 'welcome': return renderWelcome();
      case 'method': return renderMethodChoice();
      case 'analysis_result': return renderAnalysisResult();
      case 'select_colors': return renderSelectColors();
      case 'bracelet_recommend': return renderBraceletRecommend();
      case 'crystal_select': return renderCrystalSelect();
      case 'specs': return renderSpecs();
      case 'wrist': return renderWristSize();
      default: return null;
    }
  };

  // ==================== 固定 Footer ====================

  const hasFooter = currentKey !== 'welcome' && currentKey !== 'complete';

  const renderFooter = () => {
    let prevLabel = '上一步';
    let nextLabel = '下一步';
    let nextIcon = <ArrowRightOutlined />;
    let nextDisabled = false;
    let nextAction = goNext;
    let prevAction = goPrev;
    let showNext = true;

    switch (currentKey) {
      case 'method':
        // 五行/靈數需先填生日（就地展開），按「開始分析」即算結果並前進
        if (colorMethod === 'wuxing') {
          nextLabel = '開始分析';
          nextDisabled = !birthday || !birthTime;
          nextAction = doWuxingCalc;
        } else if (colorMethod === 'numerology') {
          nextLabel = '開始分析';
          nextDisabled = !birthday;
          nextAction = doNumerologyCalc;
        } else {
          nextDisabled = !colorMethod; // 功效導向直接下一步
        }
        break;
      case 'analysis_result':
        // 分析結果頁直接選配色策略，選了才能前進
        nextDisabled = !colorStrategy;
        nextLabel = '選擇顏色';
        break;
      case 'select_colors':
        nextDisabled = selectedColors.length === 0;
        nextLabel = selectedColors.length === 0 ? '請至少選一個顏色' : '看推薦款式';
        break;
      case 'bracelet_recommend':
        showNext = false; // step has its own buttons
        break;
      case 'crystal_select':
        nextLabel = '下一步';
        nextAction = handleCrystalNext; // 送出時檢查未選水晶的顏色
        // 若沒有任何推薦款式（自動跳來），上一步直接回選色
        if (braceletRecsLoaded && braceletRecs.length === 0) prevAction = () => goToStep('select_colors');
        break;
      case 'wrist':
        nextDisabled = !wristSize;
        // 選了現有款式（跳過選水晶）→ 上一步回推薦頁
        if (selectedBracelet) prevAction = () => goToStep('bracelet_recommend');
        break;
      case 'specs':
        // 設計完成 → 加入購物車（後續走共用購物車結帳）
        nextIcon = <ShoppingCartOutlined />;
        nextAction = handleAddToCart;
        nextDisabled = !chainStyle || !circleDesign || !wristSize || !termsAgreed;
        nextLabel = !chainStyle || !circleDesign ? '請選好款式與圈數'
          : !wristSize ? '請選擇手圍尺寸'
          : !termsAgreed ? '請先勾選同意條款'
          : `加入購物車 NT$ ${orderAmountBreakdown.item}`;
        break;
      default: return null;
    }

    return (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid rgba(34,41,87,0.12)',
        boxShadow: '0 -2px 16px rgba(0,0,0,0.06)',
        padding: '12px 16px', zIndex: 10,
      }}>
        {/* 客製化條款：法定告知＋強制勾選同意（不勾無法加入購物車） */}
        {currentKey === 'specs' && (
          <div style={{ maxWidth: 600, margin: '0 auto 8px' }}>
            <div style={{ fontSize: 11, color: '#7A85CD', lineHeight: 1.5, marginBottom: 6 }}>
              本商品為客製化商品，不適用消費者保護法第十九條七日猶豫期規定，請確認您的需求後再下單購買。
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, lineHeight: 1.55, cursor: 'pointer', color: '#2C3674' }}>
              <input
                type="checkbox"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: '#222957' }}
              />
              <span>我已閱讀並同意本商品為客製化訂製，下單後不可取消，且不適用 7 日鑑賞期退貨。</span>
            </label>
          </div>
        )}
        <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <Button size="large" onClick={prevAction} icon={<ArrowLeftOutlined />} style={{ height: 48 }}>{prevLabel}</Button>
          {showNext && (
            <Button
              type="primary"
              size="large"
              block
              disabled={nextDisabled}
              loading={currentKey === 'specs' ? submitting : false}
              onClick={nextAction}
              style={{ height: 48 }}
            >
              {nextLabel} {nextIcon}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // 電腦版右側「即時預覽 / 摘要」面板（專櫃感）：唯讀顯示目前選擇，隨捲動固定
  const PREVIEW_METHOD = { wuxing: '五行分析', numerology: '生命靈數', effect: '心願功效' };
  const PREVIEW_CHAIN = { elastic: '彈性繩', adjustable: '調節鍊', magnetic: '磁扣款' };
  const PREVIEW_CIRCLE = { single: '單圈', double: '雙圈' };
  const renderPreviewPanel = () => {
    const detail = { chain_style: chainStyle, circle_design: circleDesign, wrist_size: wristSize };
    const hasSpec = !!(chainStyle || circleDesign || wristSize != null);
    const price = pricing ? computeCustomPrice(detail, pricing) : null;
    const previewImg = formImages.step_welcome || formImages.hero || null;
    const row = (label, value) => (value ? (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px dashed rgba(34,41,87,0.12)' }}>
        <Text type="secondary" style={{ fontSize: 12.5, flex: '0 0 auto' }}>{label}</Text>
        <span style={{ fontSize: 12.5, textAlign: 'right', color: '#222A5A' }}>{value}</span>
      </div>
    ) : null);
    return (
      <div style={{ position: 'sticky', top: 96 }}>
        <Card styles={{ body: { padding: 18 } }} style={{ borderRadius: 18 }}>
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            {previewImg
              ? <img src={previewImg} alt="" style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 12 }} onError={(e) => { e.target.style.display = 'none'; }} />
              : <div style={{ height: 120, borderRadius: 12, background: 'linear-gradient(135deg,#F0F1F8,#ECEEF7)' }} />}
            <Title level={5} style={{ margin: '12px 0 0', color: '#222A5A', letterSpacing: 1 }}>您的專屬手鏈</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>依您的選擇即時更新</Text>
          </div>
          {row('命理方式', colorMethod ? (PREVIEW_METHOD[colorMethod] || colorMethod) : null)}
          {row('色調風格', stylePreference)}
          {selectedColors.length > 0 && (
            <div style={{ padding: '7px 0', borderBottom: '1px dashed rgba(34,41,87,0.12)' }}>
              <Text type="secondary" style={{ fontSize: 12.5, display: 'block', marginBottom: 4 }}>選擇顏色</Text>
              <Space size={[4, 4]} wrap>{selectedColors.map((c) => <Tag key={c} color="geekblue" style={{ margin: 0 }}>{c}</Tag>)}</Space>
            </div>
          )}
          {selectedCrystals.length > 0 && row('選定水晶', selectedCrystals.map((c) => c.crystal_name || c.name).filter(Boolean).join('、'))}
          {hasSpec && row('鍊形', chainStyle ? PREVIEW_CHAIN[chainStyle] : null)}
          {hasSpec && row('圈數', circleDesign ? PREVIEW_CIRCLE[circleDesign] : null)}
          {hasSpec && row('手圍', wristSize != null ? `${wristSize} cm` : null)}
          {price != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12.5 }}>預估金額</Text>
              <Text style={{ color: 'var(--brand-primary)', fontWeight: 800, fontSize: 20 }}>NT$ {price}</Text>
            </div>
          )}
          {!colorMethod && !stylePreference && selectedColors.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12.5 }}>開始選擇後，這裡會即時顯示您的手鏈設計摘要。</Text>
          )}
        </Card>
      </div>
    );
  };

  const showDesktopPreview = !isMobile && currentKey !== 'welcome' && currentKey !== 'complete' && !payResult;

  return (
    <ConfigProvider theme={{ token: { colorPrimary: theme.primary, colorTextSecondary: '#595959', colorTextDescription: '#595959' } }}>
    <div className={previewLite ? 'custom-root custom-lite' : 'custom-root'} style={{ minHeight: '100vh', background: '#fafafa', '--brand-primary': theme.primary, '--brand-secondary': theme.secondary }}>
      <style>{`
        /* 與命理詳細頁一致的「霧面玻璃」卡片效果 */
        .custom-root .ant-card { background: rgba(255,255,255,0.4); -webkit-backdrop-filter: blur(2.5px); backdrop-filter: blur(2.5px); border: 1px solid rgba(255,255,255,0.55); box-shadow: 0 6px 22px rgba(34,41,87,0.08); }
        .custom-root .ant-collapse { background: rgba(255,255,255,0.55); border-radius: 10px; overflow: hidden; }
        .custom-root .ant-collapse-content { background: rgba(255,255,255,0.92); }
        /* 預覽模式（後台 iframe）：關掉毛玻璃避免卡頓 */
        .custom-lite .ant-card { -webkit-backdrop-filter: none !important; backdrop-filter: none !important; background: rgba(255,255,255,0.9) !important; }
        /* 流星進度條的流星頭光暈脈動 */
        @keyframes meteorPulse {
          0%, 100% { box-shadow: 0 0 7px 2px rgba(107,119,199,0.75), 0 0 14px 4px rgba(107,119,199,0.32); }
          50% { box-shadow: 0 0 11px 3px rgba(107,119,199,0.95), 0 0 22px 7px rgba(107,119,199,0.5); }
        }
        /* SVG 內寫死的品牌色 → 跟著主色 */
        .custom-root [fill="#222957"] { fill: var(--brand-primary); }
        .custom-root [stroke="#222957"] { stroke: var(--brand-primary); }
      `}</style>
      {/* 全頁背景圖 */}
      {bgImage && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: 0.4, zIndex: 0, pointerEvents: 'none',
        }} />
      )}
      {/* 品牌裝飾線 */}
      <div style={{
        height: 3, background: 'linear-gradient(90deg, var(--brand-primary), var(--brand-secondary), var(--brand-primary))',
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      }} />
      {/* SEO：頁面主標（視覺隱藏，給爬蟲/螢幕閱讀器明確 H1） */}
      <h1 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
        客製水晶手鍊 — 依五行、生命靈數與願望設計｜星河耀眼 ShiningStar Jewelry
      </h1>

      {/* 頂部進度 */}
      {!payResult && currentKey !== 'welcome' && currentKey !== 'complete' && (
        <div style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', padding: '12px 16px', borderBottom: '1px solid rgba(34,41,87,0.12)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', position: 'sticky', top: 3, zIndex: 10 }}>
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            {/* 流星進度條：流星頭隨進度往右滑過 */}
            {(() => {
              const pct = Math.round((currentStep / (steps.length - 1)) * 100);
              return (
                <div style={{ position: 'relative', height: 10, margin: '4px 0 8px', display: 'flex', alignItems: 'center' }}>
                  <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 999, background: 'rgba(34,41,87,0.12)' }} />
                  {/* 流星尾（已完成漸層） */}
                  <div style={{ position: 'absolute', left: 0, height: 4, width: `${pct}%`, borderRadius: 999, background: 'linear-gradient(90deg, rgba(148,157,214,0) 0%, var(--brand-secondary, var(--brand-secondary)) 55%, var(--brand-primary, var(--brand-primary)) 100%)', transition: 'width .6s cubic-bezier(.22,1,.36,1)' }} />
                  {/* 流星頭 */}
                  <div style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%,-50%)', transition: 'left .6s cubic-bezier(.22,1,.36,1)' }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'radial-gradient(circle, #fff 0%, var(--brand-secondary, #B9BEE4) 45%, var(--brand-primary, var(--brand-primary)) 100%)', animation: 'meteorPulse 1.6s ease-in-out infinite' }} />
                  </div>
                </div>
              );
            })()}
            <Text type="secondary" style={{ fontSize: 13 }}>
              {steps[currentStep]?.title} ({currentStep}/{steps.length - 1})
            </Text>
          </div>
        </div>
      )}

      {/* 內容：電腦版採「左表單 + 右即時預覽」兩欄；手機維持單欄 */}
      <div style={{ paddingTop: 16, paddingBottom: hasFooter ? 88 : 40, position: 'relative', zIndex: 1 }}>
        {showDesktopPreview ? (
          <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 28, alignItems: 'start' }}>
            <div style={{ minWidth: 0 }}>{renderStep()}</div>
            {renderPreviewPanel()}
          </div>
        ) : renderStep()}
      </div>

      {/* 固定底部導航 */}
      {hasFooter && !payResult && renderFooter()}
    </div>
    </ConfigProvider>
  );
}
